import {
  ProjectNavbarPageId,
  ProjectNavbarSubpageId,
  TopLevelProjectNavbarLink,
} from "@/components/navigation/ProjectNavbarCommon.constants";
import {
  SETTINGS_NAV_ICONS,
  SETTINGS_SIDEBAR_ITEMS,
  SettingSidebarItem,
  type SettingsNavbarId,
} from "@/components/settings/settings.constants";
import { Routes } from "@/utils/routes";

export const matchesResourceGroupJobsRoute = (
  normalizedPath: string,
  proj: string,
  env: string,
) => {
  return (
    normalizedPath.startsWith(`${Routes.project.environment.root(proj, env)}/rgs/`) &&
    normalizedPath.endsWith("/jobs")
  );
};

export const PROJECT_NAVBAR_V2_SECTION_ORDER = [
  {
    id: "general",
    label: "General",
    itemIds: [
      "overview",
      "catalog",
      "deployments",
      "online",
      "offline",
      "explorer-sql",
      "notebooks-nav",
      "compute",
      "models",
    ],
  },
  {
    id: "monitoring",
    label: "Monitoring",
    itemIds: ["dashboards-nav", "logs", "traces-nav", "errors", "alerting"],
  },
  {
    id: "agents",
    label: "Agents",
    itemIds: ["model-gateway-nav", "mcp-gateway-nav", "agent-studio-nav", "evaluations"],
  },
  {
    id: "configuration",
    label: "Configuration",
    itemIds: ["integrations", "infrastructure", "settings"],
  },
] as const;

export const PROJECT_NAVBAR_LINKS: TopLevelProjectNavbarLink<
  ProjectNavbarPageId,
  ProjectNavbarSubpageId
>[] = [
  {
    href: (proj: string, env: string) => Routes.project.environment.root(proj, env),
    text: "Overview",
    id: "overview",
    subpages: [],
    searchTerms: ["overview"],
    icon: "home",
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.resolvers(proj, env),
    text: "Definitions",
    id: "catalog",
    icon: "libraryBig",
    subpages: [
      {
        href: (proj: string, env: string) => Routes.project.environment.resolvers(proj, env),
        text: "Resolvers",
        id: "resolvers",
        searchTerms: ["resovlers"],
        icon: "resolver",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.features(proj, env),
        text: "Features",
        id: "features",
        icon: "feature",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.featureStudio(proj, env),
        text: "Feature Studio",
        id: "feature-studio",
        icon: "computer",
        flag: "feature_studio",
        admin: true,
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.namedQueries(proj, env),
        id: "named-queries",
        text: "Named queries",
        icon: "docs",
      },
      {
        href: (proj: string, env: string) =>
          Routes.project.environment.allMetaQueriesAdminOnly(proj, env),
        id: "meta-queries",
        text: "Meta queries",
        admin: true,
        icon: "inspectionPanel",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.dataLineage(proj, env),
        text: "Data lineage",
        id: "data-lineage",
        icon: "share2",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.dataCatalog(proj, env),
        text: "Data catalog",
        id: "data-catalog",
        icon: "libraryBig",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.catalogOverview(proj, env),
        text: "Catalog overview",
        id: "catalog-overview",
        icon: "boxes",
        admin: true,
      },
    ],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.deployments(proj, env),
    text: "Deployments",
    id: "deployments",
    icon: "sailboat",
    subpages: [
      {
        href: (proj: string, env: string) => Routes.project.environment.deployments(proj, env),
        text: "All deployments",
        id: "all-deployments",
        icon: "split",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.branches(proj, env),
        text: "Branches",
        id: "branches",
        icon: "gitBranch",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.sourceCode(proj, env),
        text: "Source code",
        id: "source-code",
        icon: "code",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.argoWorkflows(proj, env),
        text: "Build workflows",
        id: "build-workflows",
        icon: "listTree",
      },
    ],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.metaQueryRuns(proj, env),
    text: "Online",
    id: "online",
    icon: "online",
    subpages: [
      {
        href: (proj: string, env: string) => Routes.project.environment.metaQueryRuns(proj, env),
        id: "online-queries",
        text: "Online queries",
        icon: "chartNoAxesGantt",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.dataExplorer(proj, env),
        id: "explorer-online",
        text: "Online explorer",
        icon: "compass",
        hideFromNavigation: true,
        noSearchIndex: true,
      },
    ],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.offlineQueries(proj, env),
    text: "Offline",
    id: "offline",
    icon: "circlePile",
    subpages: [
      {
        href: (proj: string, env: string) => Routes.project.environment.offlineQueries(proj, env),
        id: "offline-queries",
        text: "Offline queries",
        admin: false,
        icon: "chartNoAxesGantt",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.runs(proj, env),
        id: "schedules",
        text: "Schedules",
        icon: "clock",
        allowNavigationWhenSelected: false,
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.datasets(proj, env),
        text: "Datasets",
        id: "datasets",
        icon: "database",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.storage(proj, env),
        text: "Storage",
        id: "storage",
        icon: "hardDrive",
        flag: "enable_offline_store_wide_table_acceleration",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.tiles(proj, env),
        text: "Tiles",
        id: "tiles",
        icon: "grid",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.scriptTasks(proj, env),
        text: "Tasks",
        id: "script-tasks",
        icon: "checkSquare",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.workflows(proj, env),
        text: "Workflows",
        id: "workflows",
        icon: "listTree",
      },
      {
        href: (proj: string, env: string) =>
          Routes.project.environment.dataExplorerOffline(proj, env),
        id: "explorer-offline",
        text: "Offline explorer",
        icon: "compass",
        hideFromNavigation: true,
        noSearchIndex: true,
      },
    ],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.models(proj, env),
    text: "Models",
    id: "models",
    icon: "box",
    flag: "model_registry_enabled",
    subpages: [
      {
        href: (proj: string, env: string) => Routes.project.environment.models(proj, env),
        id: "model-registry",
        text: "Model registry",
        admin: false,
        icon: "library",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.modelDeployments(proj, env),
        id: "model-deployments",
        text: "Model deployments",
        admin: false,
        flag: "scaling_groups_enabled",
        icon: "box",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.trainingRunsV2(proj, env),
        id: "training-runs-v2",
        text: "Training runs",
        admin: false,
        icon: "sportShoe",
      },
    ],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.dataExplorerSql(proj, env),
    text: "SQL",
    id: "explorer-sql",
    icon: "squareAsterisk",
    subpages: [
      {
        href: (proj: string, env: string) => Routes.project.environment.dataExplorerSql(proj, env),
        text: "SQL explorer",
        id: "sql-explorer",
        icon: "asterisk",
      },
      {
        href: (proj: string, env: string) =>
          Routes.project.environment.dataExplorerSqlHistory(proj, env),
        text: "SQL history",
        id: "sql-history",
        icon: "clock",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.dataframeRuns(proj, env),
        text: "DataFrame runs",
        id: "dataframe-runs",
        icon: "run",
      },
    ],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.notebooks(proj, env),
    text: "Notebooks",
    id: "notebooks-nav",
    icon: "notebook",
    flag: "notebooks_enabled",
    subpages: [],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.sandboxes(proj, env),
    text: "Compute",
    id: "compute",
    icon: "cpu",
    flag: "scaling_groups_enabled",
    subpages: [
      {
        href: (proj: string, env: string) => Routes.project.environment.sandboxes(proj, env),
        text: "Sandboxes",
        id: "sandboxes",
        icon: "box",
        flag: "scaling_groups_enabled",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.scalingGroups(proj, env),
        text: "Scaling groups",
        id: "scaling-groups",
        icon: "boxes",
        flag: "scaling_groups_enabled",
      },
      {
        href: (proj: string, env: string) =>
          Routes.project.environment.externalFunctions(proj, env),
        text: "Functions",
        id: "external-functions",
        icon: "parentheses",
        flag: "scaling_groups_enabled",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.volumes(proj, env),
        text: "Volumes",
        id: "volumes",
        icon: "hardDrive",
        flag: "scaling_groups_enabled",
      },
      {
        href: (proj: string, env: string) =>
          Routes.project.environment.customImageBuilds(proj, env),
        text: "Images",
        id: "images",
        icon: "layers",
        serverless: true,
        flag: "scaling_groups_enabled",
      },
      {
        href: (proj: string, env: string) =>
          Routes.project.environment.containerSnapshots(proj, env),
        text: "Snapshots",
        id: "container-snapshots",
        icon: "camera",
        flag: "container_snapshots_enabled",
      },
    ],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.dashboards(proj, env),
    text: "Dashboards",
    id: "dashboards-nav",
    subpages: [],
    searchTerms: ["dashboards"],
    icon: "gauge",
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.logs.index(proj, env),
    text: "Logs",
    id: "logs",
    searchTerms: ["logging", "log viewer"],
    icon: "horizontalListQuery",
    subpages: [
      {
        href: (proj: string, env: string) => Routes.project.environment.logs.index(proj, env),
        text: "All logs",
        id: "all-logs",
        icon: "list",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.logs.access(proj, env),
        text: "Access logs",
        id: "access-logs",
        icon: "trafficCone",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.logs.audit(proj, env),
        text: "Audit logs",
        id: "audit-logs",
        icon: "textSearch",
      },
    ],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.traces(proj, env),
    text: "Traces",
    id: "traces-nav",
    searchTerms: ["spans", "tracing", "ai traces", "ai tracing"],
    icon: "pawPrint",
    subpages: [
      {
        href: (proj: string, env: string) => Routes.project.environment.traces(proj, env),
        text: "Traces",
        id: "traces",
        searchTerms: ["spans", "tracing"],
        icon: "pawPrint",
      },
      {
        text: "AI traces",
        id: "ai-traces",
        href: (proj: string, env: string) => Routes.project.environment.aiTraces(proj, env),
        searchTerms: ["ai traces", "ai tracing"],
        icon: "brain",
      },
    ],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.errors(proj, env),
    text: "Query errors",
    id: "errors",
    icon: "bug",
    subpages: [],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.monitors(proj, env),
    text: "Alerting",
    id: "alerting",
    icon: "alarmClock",
    subpages: [
      {
        href: (proj: string, env: string) => Routes.project.environment.monitors(proj, env),
        id: "monitors",
        text: "Monitors",
        icon: "computer",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.incidents(proj, env),
        id: "incidents",
        text: "Incidents",
        icon: "alertOctagon",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.alertConfig(proj, env),
        id: "alert-config",
        text: "Alert channels",
        icon: "notifications",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.webhooks(proj, env),
        id: "webhooks",
        text: "Webhooks",
        icon: "webhook",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.globalMetrics(proj, env),
        id: "global-metrics",
        text: "Metric charts",
        icon: "chart",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.metricsExplorer(proj, env),
        id: "metrics-explorer",
        text: "Metrics explorer",
        icon: "programmingBrowserSearch",
        searchTerms: ["metrics", "explore", "chart"],
      },
      {
        href: (proj: string, env: string) =>
          Routes.project.environment.settings(proj, env, "debug-chart"),
        id: "debug-chart",
        text: "Debug chart",
        icon: "bug",
        admin: true,
      },
    ],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.kubernetes(proj, env),
    serverlessHref: (proj: string, env: string) => Routes.project.environment.kubeEvents(proj, env),
    text: "Infrastructure",
    id: "infrastructure",
    icon: "hardDrive",
    subpages: [
      {
        href: (proj: string, env: string) => Routes.project.environment.kubernetes(proj, env),
        text: "Kubernetes",
        id: "kubernetes",
        searchTerms: ["pods", "nodes", "service accounts"],
        icon: "shipWheel",
        serverless: true,
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.kubeEvents(proj, env),
        text: "Kube events",
        id: "kube-events",
        searchTerms: ["kubernetes events", "k8s events"],
        icon: "audioLines",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.nodepools(proj, env),
        text: "Nodepools",
        id: "nodepools",
        searchTerms: ["karpenter"],
        icon: "vectorSquare",
        serverless: true,
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.resources(proj, env),
        text: "Resources",
        id: "resources",
        icon: "wrench",
      },
      {
        href: (proj: string, env: string) =>
          Routes.project.environment.resourceGroupJobs(proj, env, "default"),
        text: "Resource group jobs",
        id: "resource-group-jobs",
        searchTerms: ["job queue", "resource group job queue"],
        icon: "listVideo",
        routeMatch: matchesResourceGroupJobsRoute,
        serverless: true,
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.cloudOverview(proj, env),
        text: "Cloud overview",
        id: "cloud-overview",
        searchTerms: ["architecture", "cloud architecture", "cloud resources"],
        icon: "cloudServer",
        serverless: true,
      },
      {
        href: (proj: string, env: string) =>
          Routes.project.environment.artifactRepositories(proj, env),
        text: "Artifact repositories",
        id: "artifact-repositories",
        icon: "package",
        serverless: true,
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.metricsDatabase(proj, env),
        text: "Metrics Database",
        id: "metrics-database",
        icon: "database",
        environmentResourceIdField: "clusterTimescaledbId",
        serverless: true,
      },
      {
        href: (proj: string, env: string) =>
          Routes.project.environment.backgroundPersistence(proj, env),
        text: "Background Persistence",
        id: "background-persistence",
        icon: "hardDrive",
        environmentResourceIdField: "backgroundPersistenceDeploymentId",
        serverless: true,
      },
      {
        href: (proj: string, env: string) =>
          Routes.project.environment.workflowOrchestratorEngine(proj, env),
        text: "Workflow Orchestrator Engine",
        id: "workflow-orchestrator-engine",
        icon: "route",
        environmentResourceIdField: "clusterWorkflowOrchestratorId",
        serverless: true,
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.clickhouse(proj, env),
        id: "clickhouse",
        text: "ClickHouse info",
        icon: "database",
        searchTerms: ["clickhouse", "otel", "telemetry", "ttl", "storage", "logs", "traces"],
        serverless: true,
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.buildProfile(proj, env),
        text: "Build profile",
        id: "build-profile",
        icon: "radar",
        admin: true,
      },
    ],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.dataSources(proj, env),
    text: "Integrations",
    id: "integrations",
    icon: "plug",
    subpages: [
      {
        href: (proj: string, env: string) => Routes.project.environment.dataSources(proj, env),
        text: "Data sources",
        id: "data_sources",
        icon: "database",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.onlineStore(proj, env),
        text: "Online store",
        id: "online-store",
        icon: "online",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.offlineStore(proj, env),
        text: "Offline store",
        id: "offline-store",
        icon: "circlePile",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.vectorDB(proj, env),
        text: "Vector store",
        id: "vector-db",
        searchTerms: ["vector database", "vector db"],
        icon: "arrowUp10",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.secrets(proj, env),
        text: "Secrets & variables",
        id: "secrets",
        icon: "bookKey",
        searchTerms: [
          "secrets",
          "variables",
          "environment variables",
          "env vars",
          "runtime variables",
          "runtime environment",
        ],
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.envVariables(proj, env),
        text: "Config variables",
        id: "environment-variables",
        searchTerms: [
          "environment variables",
          "config variables",
          "configuration variables",
          "env vars",
        ],
        icon: "environment",
      },
    ],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.modelGateway(proj, env),
    text: "Model Gateway",
    id: "model-gateway-nav",
    icon: "route",
    flag: "router_enabled",
    searchTerms: [
      "router",
      "model routes",
      "routing",
      "access",
      "providers",
      "connections",
      "routes",
      "sampling",
      "api keys",
      "usage pools",
      "usage budgets",
      "rate limits",
      "usage",
      "exporters",
      "telemetry",
      "playground",
      "fallback policy",
      "judge policy",
      "braintrust",
      "opentelemetry",
      "otlp",
    ],
    subpages: [],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.mcpGateway(proj, env),
    text: "MCP Gateway",
    id: "mcp-gateway-nav",
    icon: "server",
    flag: "mcp_gateway_enabled",
    searchTerms: ["MCP servers", "policies", "simulator", "audit"],
    subpages: [],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.agentTemplates(proj, env),
    text: "Agent Studio",
    id: "agent-studio-nav",
    icon: "brain",
    flag: "agent_studio_enabled",
    subpages: [
      {
        href: (proj: string, env: string) => Routes.project.environment.agentTemplates(proj, env),
        text: "Templates",
        id: "agent-templates",
        icon: "layers",
      },
      {
        href: (proj: string, env: string) => Routes.project.environment.agentBuilder(proj, env),
        text: "Builder",
        id: "agent-builder",
        icon: "wrench",
        admin: true,
      },
    ],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.evaluations(proj, env),
    text: "Evaluations",
    id: "evaluations",
    icon: "bullseye",
    flag: "evaluations_enabled",
    strictFlag: true,
    subpages: [
      {
        href: (proj: string, env: string) => Routes.project.environment.evaluations(proj, env),
        text: "Suites",
        id: "evaluation-suites",
        searchTerms: ["evals"],
        icon: "bullseye",
        flag: "evaluations_enabled",
        strictFlag: true,
      },
    ],
  },
  {
    href: (proj: string, env: string) => Routes.project.environment.settings(proj, env),
    text: "Settings",
    id: "settings",
    icon: "cog",
    subpages: SETTINGS_SIDEBAR_ITEMS.flatMap((group) =>
      group.panes
        .filter((pane: SettingSidebarItem) => !pane.hideFromSettingsMenu)
        .map((pane: SettingSidebarItem) => ({
          href: (proj: string, env: string) =>
            Routes.project.environment.settings(proj, env, pane.id),
          text: pane.name,
          id: pane.id as ProjectNavbarSubpageId,
          icon: SETTINGS_NAV_ICONS[pane.id as SettingsNavbarId],
          menuGroupLabel: group.title,
          admin: pane.chalkEmployeeOnly,
          deprecationNotice: pane.deprecationNotice,
          flag: pane.flag ?? undefined,
          serverless: pane.hideInServerless,
          noSearchIndex: true, // settings are indexed separately
          routeMatch:
            pane.id === "assistant"
              ? (path: string, proj: string, env: string) => {
                  const root = Routes.project.environment.root(proj, env);
                  return (
                    path === `${root}/agent-conversations` ||
                    path.startsWith(`${root}/agent-conversations/`) ||
                    path === `${root}/settings/ai-connections`
                  );
                }
              : undefined,
        })),
    ),
  },
];
